import type { Storage } from '@chess/api-storage';

// The chess.com account check and the saved username. The settings file is
// the one plugin-settings-chesscom keeps: `settings/chesscom.json`.
const FILE = 'chesscom.json';

export interface ChesscomSettings {
  schemaVersion: 1;
  username: string;
}

export type Lookup =
  | { kind: 'found'; username: string; title?: string }
  | { kind: 'missing' }
  | { kind: 'failed'; message: string };

export function normalizeUsername(draft: string): string {
  return draft.trim().toLowerCase();
}

export async function readUsername(settings: Storage): Promise<string> {
  const text = await settings.readText(FILE);
  if (!text) return '';
  const raw = JSON.parse(text) as Partial<ChesscomSettings>;
  return raw.schemaVersion === 1 && typeof raw.username === 'string' ? raw.username : '';
}

export async function saveUsername(settings: Storage, username: string): Promise<void> {
  const record: ChesscomSettings = { schemaVersion: 1, username };
  await settings.write(FILE, JSON.stringify(record));
}

/** Asks chess.com whether the player exists: one call to the public profile endpoint. */
export async function lookupPlayer(
  username: string,
  fetchFn: typeof fetch = fetch,
): Promise<Lookup> {
  if (!username) return { kind: 'missing' };
  try {
    const res = await fetchFn(`https://api.chess.com/pub/player/${encodeURIComponent(username)}`);
    if (res.status === 404) return { kind: 'missing' };
    if (!res.ok) return { kind: 'failed', message: `chess.com answered ${res.status}` };
    const body = (await res.json()) as { username?: string; title?: string };
    return { kind: 'found', username: body.username ?? username, title: body.title };
  } catch (e) {
    return { kind: 'failed', message: e instanceof Error ? e.message : String(e) };
  }
}
